import FormTextAreaField from 'components/ui/FormTextAreaField'
import FormDateField from 'components/ui/FormDateField'
import { Form } from 'react-final-form'
import FormError from 'components/ui/FormError'
import Button from 'components/ui/Button'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SubmissionErrors } from 'final-form'
import FieldSpacer from 'components/ui/FieldSpacer'
import focusOnError from 'components/ui/focusOnError'
import FormTextField from 'components/ui/FormTextField'
import useUser, { User } from 'queries/useUser'
import LoginModal from 'components/LoginModal'
import Loading from 'components/Loading'
import FormMoneyField from 'components/ui/FormMoneyField'
import arrayMutators from 'final-form-arrays'
import every from 'lodash/every'
import CompleteProfileModal from 'components/CompleteProfileModal'
import { OptionGroup } from 'components/ui/DeepRadioGroup'
import FormDeepCardRadioGroup from 'components/ui/FormDeepCardRadioGroup'
import { cn } from 'cn'
import FormMealPartsField from 'components/ui/FormMealPartsField'
import FormSelectField from 'components/ui/FormSelectField'
import FormValuesRecorder from 'components/FormValuesRecorder'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon
} from '@heroicons/react/24/solid'
import FormAddressField from 'components/ui/FormAddressField'
import { logEvent } from 'utils/analytics'
import FormTimeField from 'components/ui/FormTimeField'
import { useCateringRequestBudgetValidation } from 'components/pages/CateringRequestFormPage/types/CateringRequestBudgetValidation'
import Link from 'next/link'
import FormSteps from 'components/ui/FormSteps'
import { createPortal } from 'react-dom'
import useTheme from 'lib/theme/useTheme'

import { CateringPreferredContactMethod } from 'global-graphql-types'

import NavigateToErrorStep, { StepFields } from './NavigateToErrorStep'
import { useMealPartsQuery } from './types/MealPartsQuery'

export type FormValues = {
  cateringType: string
  address: string
  additionalInstructions?: string
  foodPreferences?: string
  date: Date
  startAt: Date
  endAt: Date
  attendees: string
  customerTotalBudget: number
  mealParts: {
    id: number
    name: string
    count: number
  }[]
  quoteOnly: boolean
  preferredContactMethod: CateringPreferredContactMethod
}

type FormField = keyof FormValues

const cateringFormStepFields: StepFields = [
  {
    step: 'address',
    fields: [
      'cateringType',
      'address',
      'date',
      'startAt',
      'endAt',
      'requestTooFarInAdvance'
    ]
  },
  {
    step: 'food',
    fields: [
      'attendees',
      'customerTotalBudget',
      'mealParts',
      'recommendDropOff'
    ]
  },
  {
    step: 'confirmation',
    fields: [
      'preferredContactMethod',
      'additionalInstructions',
      'quoteOnly',
      'requestInProgress'
    ]
  }
]

const validate = (step: Step) => (values: Partial<FormValues>) => {
  const errors: Partial<
    Record<
      FormField,
      string | Partial<Record<keyof FormValues['mealParts'][0], string>>[]
    >
  > = {}
  let requiredFields: FormField[] = ['cateringType', 'address', 'date']
  if (step === 'food') {
    requiredFields = [
      ...requiredFields,
      'attendees',
      'customerTotalBudget',
      'mealParts'
    ]
    if (every(values.mealParts, (mp) => mp.count <= 0)) {
      errors.mealParts = [
        {
          count: 'Please choose what your guests want'
        }
      ]
    }
  }
  if (step === 'confirmation') {
    requiredFields = [...requiredFields, 'preferredContactMethod']
    if (values.quoteOnly === null || typeof values.quoteOnly === 'undefined') {
      errors.quoteOnly =
        'Please select if you are looking for a quote or ready to book now'
    }
  }

  requiredFields.forEach((key) => {
    if (!values[key]) {
      errors[key] = 'Required'
    }
  })
  if (!values.startAt) {
    errors.startAt = 'Required, e.g. 10:00 AM'
  }
  if (!values.endAt) {
    errors.endAt = 'Required, e.g. 10:00 AM'
  }
  if (values.attendees && parseInt(values.attendees, 10) <= 0) {
    errors.attendees = 'Must be a positive number'
  }
  if (values.customerTotalBudget && Number(values.customerTotalBudget) <= 0) {
    errors.customerTotalBudget = 'Must be a positive number'
  }
  return errors
}

export type Props = {
  onSubmit: (
    values: FormValues,
    user: User
  ) => Promise<SubmissionErrors | undefined>
  categoryOptions: OptionGroup<string>[]
  initialCateringType?: string
  initialValues?: Partial<FormValues>
}

type Step = 'address' | 'food' | 'confirmation'
type Tab = {
  name: string
  value: Step
}

const TABS: Tab[] = [
  { name: 'Time and Address', value: 'address' },
  { name: 'Food and Guests', value: 'food' },
  { name: 'Review & Submit', value: 'confirmation' }
]

export default function CateringRequestStepsForm({
  onSubmit,
  categoryOptions,
  initialCateringType,
  initialValues
}: Props) {
  const { theme } = useTheme()
  const dark = theme.page.themedBackground
  const [step, setStepRaw] = useState<Step>('address')
  const buttonsContainerRef = useRef<HTMLDivElement>(null)
  const setStep = (s: Step) => {
    setStepRaw((oldStep) => {
      if (oldStep !== s) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
      return s
    })
  }
  const { user } = useUser({ skip: typeof window === 'undefined' })
  const [showLogin, setShowLogin] = useState<boolean>(false)
  const [showCateringSelect, setShowCateringSelection] =
    useState<boolean>(!initialCateringType)
  useEffect(() => {
    if (initialCateringType) {
      setShowCateringSelection(false)
    }
  }, [initialCateringType])
  const [showCompleteProfile, setShowCompleteProfile] = useState<boolean>(false)

  const { data } = useMealPartsQuery({
    fetchPolicy: 'cache-first'
  })

  const mealOptions = useMemo(() => {
    if (!data) return []

    return data.upsellTypes
      .filter((mealPart) => mealPart.showInCatering)
      .map((type) => {
        return {
          id: type.id,
          name: type.cateringName,
          count: 0
        }
      })
  }, [data])

  const initialValuesMemo = useMemo(
    () => ({
      mealParts: mealOptions,
      cateringType: initialCateringType,
      ...initialValues
    }),
    [initialCateringType, initialValues, mealOptions]
  )

  const tabValues = TABS.map((t) => t.value)

  const onNext = () => {
    setStep(tabValues[tabValues.indexOf(step) + 1])
  }

  const onBack = () => {
    setStep(tabValues[tabValues.indexOf(step) - 1])
  }

  const { refetch: validateCateringRequestBudget } =
    useCateringRequestBudgetValidation({ skip: true })

  useEffect(() => {
    logEvent('form_checkout_progress', {
      form_step: step,
      form_name: 'catering_request'
    })
  }, [step])

  if (!data) return <Loading />

  const validateBudget = async (values: Partial<FormValues>) => {
    const {
      data: { cateringRequestBudgetValidation: budgetError }
    } = await validateCateringRequestBudget({
      address: values.address,
      attendees: values.attendees ? parseInt(values.attendees, 10) : 0,
      mealParts: values.mealParts,
      cateringType: values.cateringType,
      dropOff: false,
      customerTotalBudgetCents: values.customerTotalBudget
        ? values.customerTotalBudget
        : 0
    })

    return budgetError
  }

  return (
    <Form<FormValues>
      decorators={[focusOnError]}
      initialValues={initialValuesMemo}
      mutators={{
        ...arrayMutators
      }}
      onSubmit={async (values) => {
        if (step === 'food') {
          const budgetError = await validateBudget(values)
          if (!budgetError) {
            onNext()
            return {}
          }
          if (
            budgetError.key === 'recommendDropOff' ||
            budgetError.key === 'recommend_drop_off'
          ) {
            return {
              budgetError: (
                <>
                  {budgetError.message}
                  <br />
                  <br />
                  <Link
                    className="underline font-medium"
                    href={`/catering-drop-off/form?copyFromJson=${encodeURIComponent(
                      JSON.stringify({
                        ...values,
                        startAt: values.startAt?.toISOString(),
                        endAt: values.endAt?.toISOString()
                      })
                    )}`}
                  >
                    Submit a drop off request
                  </Link>
                </>
              )
            }
          }
          return {
            budgetError: budgetError.message
          }
        }
        if (step === 'address') {
          onNext()
          return {}
        }

        if (!user) {
          setShowLogin(true)
          return {}
        }
        if (!user.completeProfile) {
          setShowCompleteProfile(true)
          return {}
        }
        return onSubmit(values, user)
      }}
      validate={validate(step)}
    >
      {({ handleSubmit, submitting, form, values }) => (
        <form onSubmit={handleSubmit}>
          <NavigateToErrorStep
            setStep={setStep as (s: string) => void}
            stepFields={cateringFormStepFields}
          />
          <FormValuesRecorder storageKey="CATERING_FORM_VALUES" />
          <FormSteps allSteps={TABS} step={step} />
          <div className="shadow-md sm:p-10 p-6 rounded-md bg-white">
            {step === 'address' && (
              <>
                {showCateringSelect ? (
                  <div>
                    <FormDeepCardRadioGroup
                      description="Select what best describes your event:"
                      label="What type of event are you looking to cater?"
                      name="cateringType"
                      optionGroups={categoryOptions}
                      newDesign
                    />
                  </div>
                ) : (
                  <div>
                    <label
                      className="font-theme-heading font-bold text-gray-900 mb-2"
                      htmlFor="cateringType"
                    >
                      Catering occasion
                    </label>
                    <div
                      id="cateringType"
                      onClick={() => setShowCateringSelection(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          return setShowCateringSelection(true)
                        }
                        return null
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="underline font-medium bg-theme-primary-light text-theme-accent rounded-md px-2 py-1">
                        {initialCateringType}
                      </span>{' '}
                      <span className="italic">(click to update)</span>
                    </div>
                  </div>
                )}

                <FieldSpacer variant="normal" />
                <div>
                  <FormAddressField
                    description="Where is the event going to take place? If you don't have a location yet, enter a nearest address in that zip code."
                    label="Address"
                    name="address"
                    placeholder="Start typing..."
                    newDesign
                  />
                </div>
                <FieldSpacer variant="wide" />
                <div className="flex flex-wrap">
                  <div className="sm:w-1/4 w-full mr-auto">
                    <FormDateField label="Event Date" name="date" newDesign />
                    <div className="sm:hidden block">
                      <FieldSpacer variant="tight" />
                    </div>
                  </div>
                  <div className="sm:w-1/4 w-1/2">
                    <FormTimeField
                      baseDate="date"
                      label="Start Time"
                      name="startAt"
                      newDesign
                    />
                  </div>
                  <div className="sm:w-1/4 w-1/2 pl-2">
                    <FormTimeField
                      baseDate="date"
                      label="End Time"
                      name="endAt"
                      newDesign
                    />
                  </div>
                </div>
                <p className="text-gray-700 text-base font-light mt-1">
                  Catered events are typically 2-3 hours of service. We
                  recommend increasing your minimum budget total if you’re
                  looking for longer service times.
                </p>
                <FormError className="mt-4" errorKey="requestTooFarInAdvance" />
                {buttonsContainerRef.current &&
                  createPortal(
                    <div className="flex justify-end py-3 mt-4 gap-4">
                      <Button
                        className="min-w-[140px] grow sm:grow-0"
                        color="primary"
                        disabled={submitting}
                        onClick={() => {
                          handleSubmit()
                        }}
                        type="button"
                        variant="gradient"
                      >
                        <span>Next</span>
                        <ChevronRightIcon className="h-6 w-6 ml-1" />
                      </Button>
                    </div>,
                    buttonsContainerRef.current
                  )}
              </>
            )}
            {step === 'food' && (
              <>
                <FormTextField
                  description='What is your "minimum" headcount? (This number can always be increased at a later time)'
                  label="How many people are you expecting to serve?"
                  name="attendees"
                  placeholder="e.g. 100"
                  type="number"
                  newDesign
                />
                <FieldSpacer variant="tight" />
                <FieldSpacer variant="tight" />
                <FormMoneyField
                  currency="$"
                  description="Enter your base minimum budget here. Proposals will be created by trucks and sent to you for this amount or higher. This total will include tax, gratuity, utensils & setup."
                  inputRightAdornment={
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <span className="text-gray-400 sm:text-sm">And up</span>
                    </div>
                  }
                  label="Total minimum budget"
                  name="customerTotalBudget"
                  newDesign
                />
                <FormError className="mt-4" errorKey="recommendDropOff" />
                <FieldSpacer variant="normal" />

                <div>
                  <p className="block mb-2 font-theme-heading font-bold text-gray-900">
                    Select what you&apos;d like each guest to eat
                  </p>
                  <p className="text-gray-700 text-base font-light mb-4">
                    For example: if you want each guest to get a burger, fries
                    and a drink,
                    <br />
                    then select <strong>1 entree</strong>,{' '}
                    <strong>1 side</strong> and <strong>1 drink</strong>
                  </p>
                  <FormMealPartsField name="mealParts" />
                </div>

                <FieldSpacer variant="normal" />
                <FormTextAreaField
                  description="i.e. burgers, mexican, pizza etc."
                  label="Cuisine preferences (optional)"
                  name="foodPreferences"
                  newDesign
                />

                <FormError className="mt-4" errorKey="budgetError" />

                {buttonsContainerRef.current &&
                  createPortal(
                    <div className="flex justify-between py-3 mt-4 gap-4">
                      <Button
                        className="min-w-[140px] grow sm:grow-0"
                        color="secondary"
                        disabled={submitting}
                        onClick={() => {
                          onBack()
                        }}
                        type="button"
                        variant="gradient"
                      >
                        <ChevronLeftIcon className="h-6 w-6 mr-1" />
                        <span>Go Back</span>
                      </Button>
                      <Button
                        className="min-w-[140px] grow sm:grow-0"
                        color="primary"
                        disabled={submitting}
                        onClick={() => {
                          handleSubmit()
                        }}
                        type="button"
                        variant="gradient"
                      >
                        <span>Next</span>
                        <ChevronRightIcon className="h-6 w-6 ml-1" />
                      </Button>
                    </div>,
                    buttonsContainerRef.current
                  )}
              </>
            )}
            {step === 'confirmation' && (
              <>
                <div>
                  <FormTextAreaField
                    description="Specific trucks names, types of cuisine, dietary needs, etc."
                    label="Additional Questions or Comments"
                    name="additionalInstructions"
                    newDesign
                  />
                </div>
                <FieldSpacer variant="wide" />
                <FormSelectField
                  description='You will still receive menu proposals and updates via email/text but if you prefer a phone call as well to discuss your event, then select "Phone"'
                  label="How do you prefer to be contacted?"
                  name="preferredContactMethod"
                  options={[
                    {
                      label: 'Phone or Email',
                      value: 'contact_by_phone_or_email'
                    },
                    { label: 'Email', value: 'contact_by_email' },
                    { label: 'Phone', value: 'contact_by_phone' },
                    { label: 'Text', value: 'contact_by_text' }
                  ]}
                  newDesign
                />
                <FieldSpacer variant="wide" />

                <FormError errorKey="requestInProgress" />
                <FormError />
                <FieldSpacer variant="normal" />
                <div className="border border-gray-200 bg-gray-50 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <InformationCircleIcon className="h-5 w-5 text-gray-500 shrink-0" />
                    <span className="font-semibold text-gray-900">
                      Before you submit
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    You only need to submit one request per event. We&apos;ll
                    share it with all available food trucks in your area so they
                    can send you proposals.
                  </p>
                  <p className="text-sm text-gray-600">
                    Not ready to book just yet? Click{' '}
                    <strong className="font-bold">Request a Quote</strong> below
                    to see sample pricing or explore options. Ready to move
                    forward? Click{' '}
                    <strong className="font-bold">Ready to Book</strong> and
                    we’ll send you formal proposals ASAP.
                  </p>
                </div>
                <FormError
                  className="mt-4"
                  errorKey="quoteOnly"
                  showWhenDirty
                />
                {buttonsContainerRef.current &&
                  createPortal(
                    <div className="flex justify-between py-3 mt-4 gap-4 flex-wrap">
                      <div className="grow sm:grow-0">
                        <Button
                          className="min-w-[140px] w-full sm:w-auto"
                          color="secondary"
                          disabled={submitting}
                          onClick={() => {
                            onBack()
                          }}
                          type="button"
                          variant="gradient"
                        >
                          <ChevronLeftIcon className="h-6 w-6 mr-1" />
                          <span>Go Back</span>
                        </Button>
                      </div>
                      <div className="grow flex justify-end sm:w-auto">
                        <Button
                          className={cn('min-w-[140px] w-full sm:w-auto', {
                            'ring-8 ring-blue-500/50':
                              values.quoteOnly === true,
                            'bg-theme-button hover:bg-theme-button-hover': dark
                          })}
                          color="default"
                          disabled={submitting}
                          onClick={() => {
                            form.change('quoteOnly', true)
                            handleSubmit()
                          }}
                          type="button"
                          variant="contained"
                        >
                          Request a quote
                        </Button>
                      </div>
                      <div className="w-full sm:w-auto">
                        <Button
                          className={cn('min-w-[140px] w-full sm:w-auto', {
                            'ring-8 ring-blue-500/50':
                              values.quoteOnly === false
                          })}
                          color="primary"
                          disabled={submitting}
                          onClick={() => {
                            form.change('quoteOnly', false)
                            handleSubmit()
                          }}
                          type="button"
                          variant="gradient"
                        >
                          <span>Ready to book</span>
                          <ChevronRightIcon className="h-6 w-6 ml-1" />
                        </Button>
                      </div>
                    </div>,
                    buttonsContainerRef.current
                  )}
              </>
            )}
            {showLogin && (
              <LoginModal
                onClose={() => setShowLogin(false)}
                onLogin={() => {
                  setShowLogin(false)
                  handleSubmit()
                }}
                open={showLogin}
                catering
              />
            )}
            {showCompleteProfile && user && (
              <CompleteProfileModal
                onClose={() => setShowCompleteProfile(false)}
                onComplete={() => {
                  setShowCompleteProfile(false)
                  handleSubmit()
                }}
                open={showCompleteProfile}
              />
            )}
          </div>
          <div ref={buttonsContainerRef} />
        </form>
      )}
    </Form>
  )
}
